import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';

const ChartsAnalyticsBI = () => {
  // Top Sponsors State
  const [topDays, setTopDays] = useState(30);
  const [topLimit, setTopLimit] = useState(50);
  const [topSponsors, setTopSponsors] = useState([]);
  const [topLoading, setTopLoading] = useState(false);
  const [topError, setTopError] = useState('');

  // Brand Shows State
  const [brandInput, setBrandInput] = useState('');
  const [brandDays, setBrandDays] = useState(365);
  const [brandShows, setBrandShows] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandError, setBrandError] = useState('');

  // Show History State
  const [showIdInput, setShowIdInput] = useState('');
  const [historyDays, setHistoryDays] = useState(180);
  const [showHistory, setShowHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Raw JSON State
  const [rawJson, setRawJson] = useState(null);
  const [copiedShowId, setCopiedShowId] = useState(null);

  const getJson = async (path) => {
    const r = await fetch(`/api/top-podcasts${path}`);
    const json = await r.json();
    if (!json.ok) throw new Error(json.error || 'Request failed');
    return json;
  };

  const loadTopSponsors = async () => {
    setTopLoading(true);
    setTopError('');
    try {
      const json = await getJson(`/bi/sponsors/top?days=${topDays}&limit=${topLimit}`);
      setTopSponsors(json.rows || []);
      setRawJson(json);
    } catch (e) {
      setTopError(e.message);
    } finally {
      setTopLoading(false);
    }
  };

  const loadBrandShows = async () => {
    if (!brandInput.trim()) {
      setBrandError('Please enter a brand name');
      return;
    }
    setBrandLoading(true);
    setBrandError('');
    try {
      const json = await getJson(`/bi/sponsors/${encodeURIComponent(brandInput)}/shows?days=${brandDays}`);
      setBrandShows(json.rows || []);
      setRawJson(json);
    } catch (e) {
      setBrandError(e.message);
    } finally {
      setBrandLoading(false);
    }
  };

  const loadShowHistory = async () => {
    if (!showIdInput.trim()) {
      setHistoryError('Please enter a show ID');
      return;
    }
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const json = await getJson(`/bi/shows/${encodeURIComponent(showIdInput)}/sponsors/history?days=${historyDays}`);
      setShowHistory(json.rows || []);
      setRawJson(json);
    } catch (e) {
      setHistoryError(e.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Auto-load top sponsors on mount
  useEffect(() => {
    loadTopSponsors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ color: '#1976d2' }}>
          📊 Charts Analytics BI
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Internal business intelligence dashboard for sponsor analytics
        </Typography>
      </Box>

      {/* Top Sponsors Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Top Sponsors (Last 30 Days)
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Days</InputLabel>
                <Select
                  value={topDays}
                  onChange={(e) => setTopDays(e.target.value)}
                  label="Days"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                  }}
                >
                  <MenuItem value={7}>7</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={90}>90</MenuItem>
                  <MenuItem value={180}>180</MenuItem>
                  <MenuItem value={365}>365</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Limit</InputLabel>
                <Select
                  value={topLimit}
                  onChange={(e) => setTopLimit(e.target.value)}
                  label="Limit"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                  }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                onClick={loadTopSponsors}
                disabled={topLoading}
                fullWidth
                sx={{ height: '40px' }}
              >
                {topLoading ? <CircularProgress size={24} /> : 'Load Top Sponsors'}
              </Button>
            </Grid>
          </Grid>

          {topError && <Alert severity="error" sx={{ mb: 2 }}>{topError}</Alert>}

          {topSponsors.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Brand</strong></TableCell>
                    <TableCell align="right"><strong>Occurrences</strong></TableCell>
                    <TableCell align="right"><strong>Unique Shows</strong></TableCell>
                    <TableCell><strong>Last Seen</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topSponsors.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{row.brand}</TableCell>
                      <TableCell align="right">{row.occurrences}</TableCell>
                      <TableCell align="right">{row.uniqueShows}</TableCell>
                      <TableCell>{new Date(row.lastSeenAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!topLoading && topSponsors.length === 0 && !topError && (
            <Typography color="text.secondary">No data available</Typography>
          )}
        </CardContent>
      </Card>

      {/* Brand Shows Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Shows Running a Specific Sponsor
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Brand"
                placeholder="e.g., BetterHelp"
                value={brandInput}
                onChange={(e) => setBrandInput(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'white' },
                    '&:hover fieldset': { borderColor: 'white' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Days</InputLabel>
                <Select
                  value={brandDays}
                  onChange={(e) => setBrandDays(e.target.value)}
                  label="Days"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                  }}
                >
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={90}>90</MenuItem>
                  <MenuItem value={180}>180</MenuItem>
                  <MenuItem value={365}>365</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                onClick={loadBrandShows}
                disabled={brandLoading}
                fullWidth
                sx={{ height: '40px' }}
              >
                {brandLoading ? <CircularProgress size={24} /> : 'Find Shows'}
              </Button>
            </Grid>
          </Grid>

          {brandError && <Alert severity="error" sx={{ mb: 2 }}>{brandError}</Alert>}

          {brandShows.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Show Title</strong></TableCell>
                    <TableCell><strong>Show ID</strong></TableCell>
                    <TableCell align="right"><strong>Occurrences</strong></TableCell>
                    <TableCell><strong>Last Seen</strong></TableCell>
                    <TableCell><strong>RSS URL</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {brandShows.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{row.showTitle || '-'}</TableCell>
                      <TableCell>
                        <Box
                          onClick={() => {
                            if (row.showId) {
                              navigator.clipboard.writeText(row.showId);
                              setCopiedShowId(row.showId);
                              setTimeout(() => setCopiedShowId(null), 2000);
                            }
                          }}
                          sx={{
                            cursor: row.showId ? 'pointer' : 'default',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: copiedShowId === row.showId ? 'success.main' : 'text.primary',
                            '&:hover': row.showId ? { color: 'primary.main' } : {},
                          }}
                        >
                          {row.showId ? (
                            <>
                              {row.showId.substring(0, 8)}...{row.showId.substring(row.showId.length - 8)}
                              {copiedShowId === row.showId && ' ✓'}
                            </>
                          ) : '-'}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{row.occurrences}</TableCell>
                      <TableCell>{new Date(row.lastSeenAt).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {row.rssUrl ? (
                          <Link href={row.rssUrl} target="_blank" rel="noopener">
                            Link
                          </Link>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!brandLoading && brandShows.length === 0 && !brandError && brandInput && (
            <Typography color="text.secondary">No shows found for "{brandInput}"</Typography>
          )}
        </CardContent>
      </Card>

      {/* Show History Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Sponsor History for a Show
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Show ID"
                placeholder="show ID (SHA-256 hash)"
                value={showIdInput}
                onChange={(e) => setShowIdInput(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'white' },
                    '&:hover fieldset': { borderColor: 'white' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Days</InputLabel>
                <Select
                  value={historyDays}
                  onChange={(e) => setHistoryDays(e.target.value)}
                  label="Days"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                  }}
                >
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={90}>90</MenuItem>
                  <MenuItem value={180}>180</MenuItem>
                  <MenuItem value={365}>365</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                onClick={loadShowHistory}
                disabled={historyLoading}
                fullWidth
                sx={{ height: '40px' }}
              >
                {historyLoading ? <CircularProgress size={24} /> : 'Load History'}
              </Button>
            </Grid>
          </Grid>

          {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}

          {showHistory.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Brand</strong></TableCell>
                    <TableCell align="right"><strong>Occurrences</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {showHistory.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{row.day}</TableCell>
                      <TableCell>{row.brand}</TableCell>
                      <TableCell align="right">{row.occurrences}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!historyLoading && showHistory.length === 0 && !historyError && showIdInput && (
            <Typography color="text.secondary">No history found for this show</Typography>
          )}
        </CardContent>
      </Card>

      {/* Raw JSON Section */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Raw JSON Output
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            For debugging or exporting data
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: '#f8f8f8',
              maxHeight: 600,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '12px',
              textAlign: 'left',
            }}
          >
            <pre style={{ margin: 0, textAlign: 'left' }}>
              {rawJson ? JSON.stringify(rawJson, null, 2) : 'No data loaded yet'}
            </pre>
          </Paper>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ChartsAnalyticsBI;
